import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(ios);

    const neverShowAgain = localStorage.getItem('pwa-prompt-never-show');
    const snoozedThisSession = sessionStorage.getItem('pwa-prompt-snoozed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    // Debug log
    console.log('🔍 InstallPrompt Debug:', {
      isIOS: ios,
      isStandalone,
      neverShowAgain,
      snoozedThisSession
    });

    if (isStandalone || neverShowAgain === 'true' || snoozedThisSession === 'true') {
      console.log('❌ Pop-up bloqueado:', { isStandalone, neverShowAgain, snoozedThisSession });
      return;
    }

    // ✅ SEMPRE mostrar pop-up após 3 segundos
    const timer = setTimeout(() => {
      console.log('✅ Mostrando pop-up de instalação');
      setShowPrompt(true);
    }, 3000);

    // Listener para capturar deferredPrompt quando disponível
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      console.log('✅ beforeinstallprompt capturado');
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('✅ PWA instalado com sucesso');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    sessionStorage.setItem('pwa-prompt-snoozed', 'true');
  };

  const handleNeverAskAgain = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-never-show', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <Card className="shadow-lg border-primary/20">
        <CardHeader className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-6 w-6"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
          <CardTitle className="flex items-center gap-2 pr-8">
            <Download className="h-5 w-5 text-primary" />
            {t('pwa.install_title')}
          </CardTitle>
          <CardDescription>
            {t('pwa.install_description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isIOS ? (
            <div className="space-y-2 text-sm">
              <p className="font-medium">{t('pwa.ios_instructions_title')}:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>{t('pwa.ios_step1')}</li>
                <li>{t('pwa.ios_step2')}</li>
                <li>{t('pwa.ios_step3')}</li>
              </ol>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('pwa.install_benefits')}
            </p>
          )}
        </CardContent>
        {!isIOS && deferredPrompt && (
          <CardFooter className="flex-col gap-2">
            <Button onClick={handleInstallClick} className="w-full">
              <Download className="mr-2 h-4 w-4" />
              {t('pwa.install_button')}
            </Button>
            <button
              onClick={handleNeverAskAgain}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('pwa.never_ask_again')}
            </button>
          </CardFooter>
        )}
        {!isIOS && !deferredPrompt && (
          <CardFooter className="flex-col gap-2">
            <p className="text-xs text-muted-foreground text-center">
              {t('pwa.browser_instructions')}
            </p>
            <button
              onClick={handleNeverAskAgain}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('pwa.never_ask_again')}
            </button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
